import React from "react";
import PropTypes from "prop-types";
import Schedule from "../../constants/Schedule";
import GTMTracker from "../../utils/tracking/GTMTracker";
import Navigation from "../../utils/Navigation";
import Dropdown from "../dropdown/Dropdown";
import classnames from "classnames";
import { getStreamUrl } from "../../utils/Route";
import { LIVE } from "../../constants/StreamTypes";
import LocalStorageUtil from "../../utils/client/LocalStorageUtil";
const {
    AUDIO_CHANNELS,
    RADIO_CHANNEL_PREFERENCE_KEY,
    DEFAULT_SCHEDULE_CHANNEL_ID,
} = Schedule;

let IS_CANADA_BLOCK_ENABLED = true;
const CANADA_COUNTRY_CODE = "CA";
const GEO_QUERY_BASE_URL = "https://location.bloomberg.com";
const GEO_QUERY_TIMEOUT_MS = 1000;
const DROPDOWN_LABEL = "Regional Stream dropdown";
const radioChannelIds = AUDIO_CHANNELS.map((channel) => channel.channelId);

// for testing purpose
export function _setIsCanadaBlockEnabled(val) {
    IS_CANADA_BLOCK_ENABLED = val;
}

class StreamsDropdown extends React.Component {
    constructor(...args) {
        super(...args);
        this.state = {
            currentChannelId: this._findCurrentChannel(
                this.props.currentChannelId
            ).channelId,
            title: this.props.title || this.props.localizer("CURRENT_STREAM"),
            channels: this.props.channels,
            cojpPath: this.props.cojpPath,
        };
        this._onItemChosen = this._onItemChosen.bind(this);
    }

    render() {
        const { currentChannelId, title, channels, cojpPath } = this.state;
        const { streamType } = this.props;
        const currentChannel = this._findCurrentChannel(currentChannelId);
        const currentChannelIndex = channels.findIndex(
            (channel) => channel.channelId === currentChannel.channelId
        );
        const channelDisplayName = this.props.localizer(
            currentChannel.displayName
        );
        const dropdownLabel = `${channelDisplayName}: ${DROPDOWN_LABEL}`;
        const dropdownTitle = (
            <React.Fragment>
                <h2 className="streams-dropdown__label">{title}:</h2>
                <span className="streams-dropdown__current-stream-name">
                    {channelDisplayName}
                </span>
            </React.Fragment>
        );

        const dropdownItems = channels.map(
            ({ channelId, displayName }, index) => (
                <a
                    key={index}
                    className={classnames("streams-dropdown__item-link", {
                        "streams-dropdown__item-link--current":
                            index === currentChannelIndex,
                    })}
                    href={getStreamUrl({
                        channelId,
                        type: streamType,
                        cojpPath,
                    })}
                    onClick={this._onStreamClick.bind(this, channelId)}
                    tabIndex="-1"
                >
                    {this.props.localizer(displayName)}
                </a>
            )
        );
        const dropdownOptions = {
            baseClassName: "streams-dropdown",
            onItemChosen: this._onItemChosen,
            selectedIndex: currentChannelIndex,
            dropdownTitle,
            dropdownItems,
            dropdownLabel,
        };

        return <Dropdown {...dropdownOptions} />;
    }

    componentDidUpdate(prevProps) {
        if (prevProps.currentChannelId !== this.props.currentChannelId) {
            this.setState({
                currentChannelId: this._findCurrentChannel(
                    this.props.currentChannelId
                ).channelId,
            });
        }
    }

    async componentDidMount() {
        if (!IS_CANADA_BLOCK_ENABLED) {
            return;
        }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const { default: MsGeoIP } = await import("ms.geoip");
        const geoIPClient = new MsGeoIP({
            url: GEO_QUERY_BASE_URL,
            timeout: GEO_QUERY_TIMEOUT_MS,
        });
        const isCanada =
            (await geoIPClient.getGeoInfo()).country === CANADA_COUNTRY_CODE;
        if (isCanada) {
            const filteredChannels = this.state.channels.filter(
                (channel) =>
                    !channel?.excludeInCountries?.includes(CANADA_COUNTRY_CODE)
            );
            this.setState({
                channels: filteredChannels,
            });
        }
    }

    _onItemChosen(index) {
        const { streamType } = this.props;
        const { channels, cojpPath } = this.state;
        const channelId = channels[index].channelId;
        this._setCurrentChannel(channelId);

        Navigation.delayNavigateToUrl(
            getStreamUrl({ channelId, type: streamType, cojpPath })
        );
    }

    _onStreamClick(channelId, event) {
        if (radioChannelIds.indexOf(channelId) >= 0) {
            LocalStorageUtil.setValue(RADIO_CHANNEL_PREFERENCE_KEY, channelId);
        }
        this._setCurrentChannel(channelId);
        Navigation.delayNavigateTo(event);
    }

    _setCurrentChannel(channelId) {
        this.setState({
            currentChannelId: channelId,
            title: this.props.localizer("CURRENT_STREAM"),
        });

        GTMTracker.trackEngagement({
            bvpEventAction: "click",
            bvpEventLabel: `${
                this.props.eventPrefix
            }.channel_dropdown_${channelId.replace("/", "_")}`,
        });
    }

    _findCurrentChannel(currentChannelId) {
        const {
            defaultChannelId,
            channelsByChannelId: {
                [currentChannelId]: currentChannel,
                [defaultChannelId]: defaultChannel,
            },
        } = this.props;
        return currentChannel || defaultChannel || this.state.channels[0];
    }
}

StreamsDropdown.defaultProps = {
    currentChannelId: DEFAULT_SCHEDULE_CHANNEL_ID,
    localizer: (input) => input,
    eventPrefix: "",
    defaultChannelId: DEFAULT_SCHEDULE_CHANNEL_ID,
    streamType: LIVE,
    cojpPath: undefined,
};

StreamsDropdown.propTypes = {
    title: PropTypes.string,
    currentChannelId: PropTypes.string,
    localizer: PropTypes.func,
    eventPrefix: PropTypes.string,
    channels: PropTypes.array.isRequired,
    defaultChannelId: PropTypes.string,
    streamType: PropTypes.string,
    channelsByChannelId: PropTypes.object.isRequired,
    cojpPath: PropTypes.string,
};

export default StreamsDropdown;
